import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(emailOrPhone, password);
      // For PWA/mobile: redirect directly to report form
      // Check if running as PWA (standalone mode) or mobile device
      const isPWA = window.matchMedia('(display-mode: standalone)').matches;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      
      if (isPWA || isMobile) {
        navigate('/report/new');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.loginFailed'));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
          <ThemeToggle />
        </Box>
        <Typography component="h1" variant="h4" gutterBottom>
          {process.env.REACT_APP_ORG_NAME || t('common.appName')}
        </Typography>
        <Paper elevation={3} sx={{ p: 4, mt: 2, width: '100%' }}>
          <Typography component="h2" variant="h5" align="center" gutterBottom>
            {t('login.signIn')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.emailOrPhone')}
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              {t('auth.loginButton')}
            </Button>
            {/* Google OAuth button - disabled until Google credentials are configured */}
            {/* <Button
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => {
                window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/google`;
              }}
            >
              Sign in with Google
            </Button> */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('login.contactAdministrator')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;

